'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, X, ArrowRight, Mail, ChevronDown } from 'lucide-react';
import { pricingTiers, guaranteeStrip } from '@/lib/pricing-display';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';

type BillingPeriod = 'monthly' | 'annual';

const comparisonCategories = [
  {
    name: 'Minutes',
    features: [
      { name: 'Included Tessa-minutes', free: '200/mo', team: '100 pooled per seat', scale: 'Custom commit' },
      { name: 'Web & room check-ins', free: 'From day one', team: 'From day one', scale: 'From day one' },
      { name: 'Phone calls', free: 'Verify your phone + card on file ($0)', team: 'After you verify your phone', scale: 'After you verify your phone' },
      { name: 'Beyond included minutes', free: '-', team: 'Top up at 15¢/min', scale: 'From 10¢/min' },
      { name: 'No-answer calls', free: 'Cost nothing', team: 'Cost nothing', scale: 'Cost nothing' },
      { name: 'Minute packs', free: '-', team: 'Never expire', scale: 'Never expire' },
      { name: 'Per-routine minute budgets', free: '-', team: 'Yes', scale: 'Yes' },
    ],
  },
  {
    name: 'People',
    features: [
      { name: 'Builders (create & edit routines)', free: '1', team: '$99/seat — owners & admins', scale: 'Custom' },
      { name: 'People Tessa calls', free: 'Unlimited, free', team: 'Unlimited, free', scale: 'Unlimited, free' },
      { name: 'Accounts needed by members', free: 'None', team: 'None', scale: 'None' },
    ],
  },
  {
    name: 'Templates & channels',
    features: [
      { name: 'Check-in templates', free: 'All 5', team: 'All 5', scale: 'All 5' },
      { name: 'Channels (calls · SMS · WhatsApp · email · Slack · web calls · calendar)', free: 'All', team: 'All', scale: 'All' },
      { name: 'SMS heads-up before first call', free: 'Yes', team: 'Yes', scale: 'Yes' },
      { name: 'Members can reply SKIP', free: 'Yes', team: 'Yes', scale: 'Yes' },
    ],
  },
  {
    name: 'Insights & supervision',
    features: [
      { name: 'Insights members (read-only supervisors)', free: '-', team: 'Included free, unlimited', scale: 'Included' },
      { name: 'Assign supervisors to some or all routines', free: '-', team: 'Yes', scale: 'Yes' },
      { name: 'Weekly supervisor digest', free: '-', team: 'Yes', scale: 'Yes' },
      { name: 'Member For-you feed', free: 'Yes', team: 'Yes', scale: 'Yes' },
      // Read-only + builder-gated by the backend (fail-closed per call) —
      // the cell says WHO gets it, mirroring the Insights row's honesty.
      // "Builder seats" matters: an admin WITHOUT a licensed seat is not
      // eligible (eligible-domains.ts — owner implicit, admin needs a seat).
      { name: 'AI assistant connector — Claude, ChatGPT & MCP clients (read-only)', free: '-', team: 'Owner & admin Builder seats', scale: 'Owner & admin Builder seats' },
    ],
  },
  {
    name: 'Content & Studio',
    features: [
      // No enforced numeric content caps — the product gates on VOICE minutes
      // and Studio seats, not post counts / recording length / storage. Say
      // "Fair use" rather than advertise a "5 posts / 45 min / 5 GB" wall no
      // code path enforces (conversion wave — don't promise walls we don't build).
      { name: 'Posts', free: 'Fair use', team: 'Unlimited', scale: 'Unlimited' },
      { name: 'Recording length', free: 'Fair use', team: 'Unlimited', scale: 'Unlimited' },
      { name: 'Editing, clipping, captions', free: '-', team: 'Yes', scale: 'Yes' },
      // Deliberately no 4K row: the render vendor caps output at 1080p today
      // (moments/export-video hard-400s quality='4k') — never advertise a
      // capability no code path can produce.
      { name: 'HD video export (1080p)', free: '-', team: 'Yes', scale: 'Yes' },
      { name: 'Storage', free: 'Fair use', team: '100 GB per seat', scale: 'Custom' },
    ],
  },
  {
    name: 'Security & support',
    features: [
      { name: 'SSO', free: '-', team: '-', scale: 'Yes' },
      { name: 'SLA', free: '-', team: '-', scale: 'Yes' },
    ],
  },
];

const faqs = [
  {
    q: "What's a minute?",
    a: 'One minute of Tessa actually talking with your team — billed by the second, never for ring time, and calls nobody answers cost nothing.',
  },
  {
    q: 'Who needs a paid seat?',
    a: "Only Builders — the founders and leaders who decide what Tessa asks and read what comes back. The people Tessa calls are members, and members are always free; supervisors who read the results are free too.",
  },
  {
    q: 'Is this employee monitoring?',
    a: "No — and it's built so it can't become that. Tessa never scores individuals. Leaders see aggregate trends, sentiment, and blockers across the team, and each person's blocker is routed to someone who can actually unblock it. For your team the deal is simple: their voice reaches leadership, their blocker reaches its solver — and anyone can say SKIP.",
  },
  {
    q: 'Why ask for a card if Free is free?',
    a: "Phone numbers and calls have real telephony costs, so we ask for a card as a safety check before your workspace gets its own number. It's $0 — no charge, no subscription, and nothing is ever charged automatically (auto top-up is a separate opt-in for Team plans). Web check-ins never need a card.",
  },
  {
    q: 'What happens when I use up my included minutes?',
    a: 'Your check-ins keep running — Tessa continues by email and Slack until you top up. Buy a minute pack (100 minutes for $15; packs never expire) or add a builder seat to grow the pool, and set per-routine budgets so no single routine drains it. There is no automatic overage billing.',
  },
  {
    q: 'Can I change plans?',
    a: 'Yes, you can upgrade or downgrade at any time. Changes take effect immediately, and billing is prorated.',
  },
  {
    q: 'Can Claude or ChatGPT read my Apprendo data?',
    a: 'On the Team plan and higher, owners and admins with Builder seats can connect Apprendo to Claude, ChatGPT, or any MCP-enabled assistant in Settings → Connectors. Access is read-only, follows the same permissions that person has inside Apprendo, and can be revoked anytime.',
  },
];

export default function PublicPricingTable() {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  // isAuthenticated is only true once auth has resolved to a signed-in user.
  const { isAuthenticated } = useSupabaseAuth();

  // The high-intent Team CTA must never dead-end. Resolve the destination from
  // (a) auth state and (b) the monthly/annual toggle so the click lands one
  // step from checkout with the plan pre-selected:
  //  - logged in  → /dashboard/settings/billing?intent=team[&period=annual]
  //                 (billing-dashboard reads intent=team + optional period and
  //                  opens Team checkout with returnTo back to billing)
  //  - logged out → /login?signup=true&next=<url-encoded billing intent path>
  //                 (getSafeRedirectPath preserves the query, so post-login the
  //                  user lands on the same intent URL). This path is also safe
  //                  for an already-authenticated user during auth loading:
  //                  /login detects the session and honors ?next=.
  const teamCtaHref = (() => {
    const period = billingPeriod === 'annual' ? '&period=annual' : '';
    const billingIntentPath = `/dashboard/settings/billing?intent=team${period}`;
    return isAuthenticated
      ? billingIntentPath
      : `/login?signup=true&next=${encodeURIComponent(billingIntentPath)}`;
  })();

  const getPrice = (tierId: string, price: string) => {
    if (tierId === 'team' && billingPeriod === 'annual') return '$990';
    return price;
  };

  const getPriceNote = (tierId: string, priceNote: string) => {
    if (tierId === 'team') {
      return billingPeriod === 'annual'
        ? 'per Builder seat / year — 2 months free'
        : 'per Builder seat / month';
    }
    return priceNote;
  };

  return (
    <div>
      <div className="flex justify-center mb-10">
        <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              billingPeriod === 'monthly'
                ? 'bg-white dark:bg-gray-700 shadow-sm text-foreground'
                : 'text-muted-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('annual')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              billingPeriod === 'annual'
                ? 'bg-white dark:bg-gray-700 shadow-sm text-foreground'
                : 'text-muted-foreground'
            }`}
          >
            Annual
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-semibold">
              2 months free
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {pricingTiers.map((tier) => (
          <Card
            key={tier.id}
            className={`p-6 flex flex-col hover:shadow-lg transition-all ${
              tier.highlight ? 'border-2 border-[#EF4444] relative' : ''
            }`}
          >
            {tier.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#EF4444] text-white text-xs font-semibold rounded-full">
                Most Popular
              </div>
            )}
            <div className="mb-6">
              <h3 className="font-bold text-xl mb-2">{tier.name}</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">{getPrice(tier.id, tier.price)}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{getPriceNote(tier.id, tier.priceNote)}</p>
            </div>

            <ul className="space-y-3 mb-4 flex-1">
              {tier.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>

            <p className="text-xs text-muted-foreground border-t pt-3 mb-6">
              People Tessa calls: unlimited, free
            </p>

            <Link href={tier.id === 'team' ? teamCtaHref : tier.cta.href}>
              <Button
                className={`w-full ${
                  tier.highlight ? 'bg-[#EF4444] hover:bg-[#DC2626] text-white' : ''
                }`}
                variant={tier.highlight ? 'default' : 'outline'}
              >
                {tier.id === 'scale' && <Mail className="w-4 h-4 mr-2" />}
                {tier.cta.label}
                {tier.id !== 'scale' && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </Link>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16 max-w-5xl mx-auto">
        {guaranteeStrip.map((item) => (
          <div key={item} className="flex items-start gap-2.5 bg-muted/50 border rounded-lg p-4">
            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-muted-foreground">{item}</span>
          </div>
        ))}
      </div>

      <div className="max-w-5xl mx-auto mb-16">
        <h3 className="text-2xl font-bold text-center mb-8">Compare plans</h3>
        <div className="space-y-2">
          {comparisonCategories.map((category) => (
            <div key={category.name} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedCategory(expandedCategory === category.name ? null : category.name)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="font-semibold">{category.name}</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${expandedCategory === category.name ? 'rotate-180' : ''}`} />
              </button>
              {expandedCategory === category.name && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium text-muted-foreground w-2/5">Feature</th>
                        <th className="text-center p-3 font-medium w-1/5">Free</th>
                        <th className="text-center p-3 font-medium text-[#EF4444] w-1/5">Team</th>
                        <th className="text-center p-3 font-medium w-1/5">Scale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.features.map((feature) => (
                        <tr key={feature.name} className="border-b last:border-0">
                          <td className="p-3 text-muted-foreground">{feature.name}</td>
                          <td className="p-3 text-center">{feature.free === '-' ? <X className="w-4 h-4 text-gray-300 mx-auto" /> : feature.free}</td>
                          <td className="p-3 text-center font-medium">{feature.team === '-' ? <X className="w-4 h-4 text-gray-300 mx-auto" /> : feature.team}</td>
                          <td className="p-3 text-center">{feature.scale === '-' ? <X className="w-4 h-4 text-gray-300 mx-auto" /> : feature.scale}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        <h3 className="text-2xl font-bold text-center mb-8">Frequently asked questions</h3>
        <div className="space-y-6">
          {faqs.map((faq) => (
            <div key={faq.q} className="border-b pb-6 last:border-0">
              <h4 className="font-semibold mb-2">{faq.q}</h4>
              <p className="text-sm text-muted-foreground">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
